import createClient from "openapi-fetch";
import type { paths } from "./schema";

/** Raised for any non-2xx answer, so callers and TanStack Query see one error type. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * The typed API client. The API is same-origin (`/api`, behind Caddy in production, the Vite proxy in dev),
 * so the session cookie is first-party and sent automatically; the browser never holds a token.
 */
export const api = createClient<paths>({
  // Absolute so Request() also resolves outside a document (tests); same origin either way.
  baseUrl: typeof location === "undefined" ? "" : location.origin,
  credentials: "same-origin",
  headers: { Accept: "application/json" },
  // Looked up per call, so a replaced fetch (tests, instrumentation) is honored.
  fetch: (request) => globalThis.fetch(request),
});

/** Turns an openapi-fetch result into its data, or throws an ApiError. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  const { data, error, response } = result;
  if (response.ok) return data as T;
  const body = (error ?? {}) as { error?: unknown; message?: unknown };
  const code = typeof body.error === "string" ? body.error : undefined;
  const message =
    typeof body.message === "string" ? body.message : `${response.status} ${response.statusText}`;
  throw new ApiError(response.status, message, code);
}
